import { NavLink } from "@/components/NavLink";
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { 
  Home, Flame, Rocket, Menu, Globe, Trophy, Radio, 
  MessageSquare, Store, Crosshair, DollarSign, UserCheck, 
  Fish, Code, HelpCircle, BookOpen, FileText, Settings, 
  Shield, Sparkles, BarChart3, LayoutDashboard, X
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useWallet } from "@/contexts/WalletContext";
import { useIsAdmin } from "@/hooks/use-admin";

const ICON_MAP: Record<string, typeof Home> = {
  home: Home,
  dashboard: LayoutDashboard,
  discover: Globe,
  trending: Flame,
  new: Sparkles,
  leaderboard: Trophy,
  portfolio: BarChart3,
  streams: Radio,
  community: MessageSquare,
  marketplace: Store,
  settings: Settings,
};

const HREF_MAP: Record<string, string> = {
  home: "/",
  dashboard: "/dashboard",
  discover: "/discover",
  trending: "/trending",
  new: "/new",
  leaderboard: "/leaderboard",
  portfolio: "/portfolio",
  streams: "/streams",
  community: "/community-hub",
  marketplace: "/marketplace",
  settings: "/settings",
};

const LABEL_MAP: Record<string, string> = {
  home: "Home",
  dashboard: "Dashboard",
  discover: "Discover",
  trending: "Trending",
  new: "New",
  leaderboard: "Leaderboard",
  portfolio: "Portfolio",
  streams: "Streams",
  community: "Community",
  marketplace: "Market",
  settings: "Settings",
};

const DEFAULT_ITEMS = ["home", "trending", "portfolio", "streams"];

const allNavItems = [
  { icon: Home, label: "Home", href: "/", section: "Main" },
  { icon: LayoutDashboard, label: "Dashboard", href: "/dashboard", section: "Main", requiresAuth: true },
  { icon: Globe, label: "Discover", href: "/discover", section: "Main" },
  { icon: Flame, label: "Trending", href: "/trending", section: "Main" },
  { icon: Sparkles, label: "New Coins", href: "/new", section: "Main" },
  { icon: Trophy, label: "Leaderboard", href: "/leaderboard", section: "Main" },
  { icon: BarChart3, label: "Portfolio", href: "/portfolio", section: "Main" },
  { icon: Radio, label: "Streams", href: "/streams", section: "Main" },
  { icon: MessageSquare, label: "Community Hub", href: "/community-hub", section: "Main" },
  { icon: Store, label: "Marketplace", href: "/marketplace", section: "Main" },
  { icon: Crosshair, label: "Token Sniper", href: "/sniper", section: "Tools", badge: "Pro" },
  { icon: DollarSign, label: "Copy Subs", href: "/copy-subscriptions", section: "Tools", badge: "Earn" },
  { icon: UserCheck, label: "Copy Trading", href: "/copy-trading", section: "Tools" },
  { icon: Fish, label: "Whale Tracker", href: "/whales", section: "Tools" },
  { icon: Radio, label: "Streamer Dashboard", href: "/streamer", section: "Tools" },
  { icon: Code, label: "API Access", href: "/api", section: "Tools" },
  { icon: HelpCircle, label: "Support", href: "/support", section: "Resources" },
  { icon: BookOpen, label: "Docs", href: "/docs", section: "Resources" },
  { icon: FileText, label: "Terms", href: "/terms", section: "Resources" },
  { icon: Settings, label: "Settings", href: "/settings", section: "Account" },
  { icon: Shield, label: "Admin", href: "/admin", section: "Account", requiresAdmin: true },
];

export function MobileBottomNav() {
  const { connected } = useWallet();
  const { isAdmin } = useIsAdmin();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [navItems, setNavItems] = useState(DEFAULT_ITEMS);
  const [showFloatingButton, setShowFloatingButton] = useState(true);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const navConfig = localStorage.getItem("moonrun_nav_config");
    if (navConfig) {
      try {
        const config = JSON.parse(navConfig);
        if (config.mobile?.bottomNavItems) {
          setNavItems(config.mobile.bottomNavItems);
        }
        if (config.mobile?.showFloatingButton !== undefined) {
          setShowFloatingButton(config.mobile.showFloatingButton);
        }
      } catch (e) {
        console.error("Failed to parse nav config:", e);
      }
    }
  }, []);

  // Always show nav - removed scroll hiding behavior for persistence
  useEffect(() => {
    setIsVisible(true);
  }, [location.pathname]);

  const sections = ["Main", "Tools", "Resources", "Account"];
  const filteredAllNavItems = allNavItems.filter(
    (item) => (!item.requiresAuth || connected) && (!item.requiresAdmin || isAdmin)
  );

  // Get actual nav items to display, filtering out dashboard if not connected
  const displayNavItems = navItems
    .filter(itemId => !(itemId === "dashboard" && !connected))
    .slice(0, 4);

  return (
    <>
      {showFloatingButton && (
        <Link
          to="/create"
          className="lg:hidden fixed right-4 bottom-24 z-40 w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg shadow-primary/30 hover:scale-110 transition-transform animate-glow"
        >
          <Rocket className="w-6 h-6" />
        </Link>
      )}

      <nav className={cn(
        "fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-background/95 backdrop-blur-lg border-t border-border transition-transform duration-300",
        isVisible ? "translate-y-0" : "translate-y-full"
      )} style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
        <div className="flex items-center justify-around h-16">
          {displayNavItems.map((itemId) => {
            const Icon = ICON_MAP[itemId];
            const href = HREF_MAP[itemId];
            const label = LABEL_MAP[itemId];
            if (!Icon || !href) return null;

            const isActive = location.pathname === href || 
              (href !== "/" && location.pathname.startsWith(href));

            return (
              <Link
                key={itemId}
                to={href}
                className={cn(
                  "flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-lg transition-all",
                  isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
                )}
              >
                <Icon className="h-5 w-5" />
                <span className="text-[10px] font-medium">{label}</span>
              </Link>
            );
          })}
          
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <button className="flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-lg text-muted-foreground hover:text-foreground transition-all">
                <Menu className="h-5 w-5" />
                <span className="text-[10px] font-medium">More</span>
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="h-[85vh] rounded-t-3xl">
              <SheetHeader className="pb-4">
                <SheetTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Rocket className="h-5 w-5 text-primary" />
                    <span className="font-display">TheMoonRun</span>
                  </div>
                </SheetTitle>
              </SheetHeader>
              
              <div className="overflow-y-auto h-[calc(100%-80px)] space-y-6 pb-8">
                {sections.map((section) => (
                  <div key={section}>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-2">
                      {section}
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      {filteredAllNavItems
                        .filter((item) => item.section === section)
                        .map((item) => {
                          const isActive = location.pathname === item.href;
                          return (
                            <Link
                              key={item.href}
                              to={item.href}
                              onClick={() => setMenuOpen(false)}
                              className={cn(
                                "flex flex-col items-center gap-2 p-3 rounded-xl transition-colors relative",
                                isActive 
                                  ? "bg-primary/10 border border-primary/30" 
                                  : "bg-secondary/50 hover:bg-secondary"
                              )}
                            >
                              <item.icon className={cn("h-5 w-5", isActive ? "text-primary" : "text-primary/70")} />
                              <span className="text-xs font-medium text-center">{item.label}</span>
                              {item.badge && (
                                <span className="absolute top-1 right-1 px-1.5 py-0.5 text-[8px] font-bold rounded bg-primary/20 text-primary">
                                  {item.badge}
                                </span>
                              )}
                            </Link>
                          );
                        })}
                    </div>
                  </div>
                ))}
                
                <div className="p-4 rounded-xl bg-gradient-to-br from-primary/20 to-accent/10 border border-primary/20">
                  <h3 className="font-display text-sm font-semibold text-primary mb-2">
                    Launch Your Coin
                  </h3>
                  <p className="text-xs text-muted-foreground mb-3">
                    Fair launch with bonding curve. No presales. Lowest fees (0.02 SOL).
                  </p>
                  <Link
                    to="/create"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center justify-center gap-2 w-full py-3 rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors"
                  >
                    <Rocket className="h-4 w-4" />
                    Get Started
                  </Link>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </>
  );
}
